import React, { useEffect, useRef, useState } from 'react';
import PrimaryModal from '../UI/Modal/PrimaryModal';
import { PrimaryButton, SecondaryButton, ErrorButton } from "../UI";
import AgGridWrapper from '../UI/AgGrid';
import { Trash2 } from 'lucide-react';
import { getProject, uploadProjectContext, deleteProjectContext } from '../../utils/APIs';

export const SUPPORTED_CONTEXT_EXTENSIONS = new Set([
    '.pdf',
    '.docx', '.docm', '.dotx', '.dotm',
    '.xlsx', '.xls', '.xlsm', '.xltx', '.xltm', '.xlsb',
    '.pptx', '.pptm', '.potx', '.potm',
    '.odt', '.ott', '.odp', '.otp', '.ods', '.ots', '.odg', '.otg',
    '.rtf',
    '.epub',
    '.md', '.txt', '.json', '.csv', '.tsv', '.yaml', '.yml', '.xml', '.html',
]);

const ACCEPT_ATTRIBUTE = Array.from(SUPPORTED_CONTEXT_EXTENSIONS).join(',');

const getExtension = (name = '') => {
    const idx = name.lastIndexOf('.');
    return idx >= 0 ? name.slice(idx).toLowerCase() : '';
};

const message = (error) => error?.response?.data?.message || error?.message || 'Unable to update project context.';

export default function ProjectContextModal({ token, organizationId, projectId, onClose, onUpdated }) {
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [reload, setReload] = useState(0);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState('');
    const request = useRef(null);
    const fileInput = useRef(null);

    useEffect(() => () => request.current?.abort(), []);
    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError('');
        getProject(token, organizationId, projectId, controller.signal)
            .then((result) => { if (!controller.signal.aborted) setProject(result.data); })
            .catch((err) => { if (!controller.signal.aborted) setError(message(err)); })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [token, organizationId, projectId, reload]);

    const mutate = async (filename) => {
        if (request.current) return;
        if (!filename && !file) return;
        if (!filename && file.size > 10 * 1024 * 1024) {
            setError('Context file must not exceed 10 MB.');
            return;
        }
        if (!filename && file) {
            const ext = getExtension(file.name);
            if (!SUPPORTED_CONTEXT_EXTENSIONS.has(ext)) {
                setError(
                    ext === '.ppt' || ext === '.doc'
                        ? `Legacy "${ext}" format is not supported. Please convert to modern ${ext === '.ppt' ? '.pptx' : '.docx'} format.`
                        : 'The selected file type is not supported.'
                );
                return;
            }
        }
        const controller = new AbortController();
        request.current = controller;
        setBusy(filename || '__upload__');
        setError('');
        try {
            const result = filename
                ? await deleteProjectContext(token, organizationId, projectId, filename, controller.signal)
                : await uploadProjectContext(token, organizationId, projectId, file, controller.signal);
            if (controller.signal.aborted) return;
            setProject(result.data);
            onUpdated(result.data);
            if (!filename) {
                setFile(null);
                if (fileInput.current) fileInput.current.value = '';
            }
        } catch (err) {
            if (!controller.signal.aborted) setError(message(err));
        } finally {
            if (!controller.signal.aborted) {
                request.current = null;
                setBusy('');
            }
        }
    };

    const onUploadChange = (event) => {
        const selected = event.target.files?.[0] || null;
        if (selected) {
            const ext = getExtension(selected.name);
            if (!SUPPORTED_CONTEXT_EXTENSIONS.has(ext)) {
                setError(
                    ext === '.ppt' || ext === '.doc'
                        ? `Legacy "${ext}" format is not supported. Please convert to modern ${ext === '.ppt' ? '.pptx' : '.docx'} format.`
                        : 'The selected file type is not supported.'
                );
                setFile(null);
                if (fileInput.current) fileInput.current.value = '';
                return;
            }
        }
        setFile(selected);
        setError('');
    }

    return (
        <PrimaryModal title={`Manage context${project ? ` — ${project.name}` : ''}`} zIndex={1300} loading={!!busy} hideModal={onClose} widthClass="!max-w-3xl">
            <div className="p-6">
                {loading ? <p role="status">Loading context files…</p> : <>
                    {project && <>
                        <form onSubmit={(event) => { event.preventDefault(); mutate(); }} className="mb-5">
                            <label htmlFor="project-context-file" className="block text-sm font-medium mb-2">Upload new context file</label>
                            <input
                                ref={fileInput}
                                id="project-context-file"
                                type="file"
                                accept={ACCEPT_ATTRIBUTE}
                                disabled={!!busy}
                                onChange={onUploadChange}
                                className="block w-full text-sm file:bg-primary file:text-white file:border-0 file:rounded-lg file:px-4 file:py-2 file:mr-3"
                            />
                            <div className="flex items-center justify-between mt-3">
                                <span className="text-xs text-gray-500">Maximum file size: 10 MB</span>
                                <PrimaryButton type="submit" size="sm" loading={busy === '__upload__'} disable={!file || !!busy}>Upload file</PrimaryButton>
                            </div>
                        </form>
                        <AgGridWrapper tableId="assistant-project-context" rowData={(project.context || []).map((filename) => ({ filename }))} getRowId={({ data }) => data.filename} rowHeight={56}
                            defaultColDef={{ sortable: true, resizable: true }}
                            columnDefs={[
                                { headerName: 'File name', field: 'filename', flex: 1, minWidth: 200, tooltipField: 'filename' },
                                { headerName: 'Delete', cellStyle: { display: 'flex', alignItems: 'center' }, width: 100, sortable: false, cellRenderer: ({ data }) => <ErrorButton size="xs" className="!min-w-0" disable={!!busy} handleClick={() => mutate(data.filename)}><Trash2 size={18} aria-hidden="true" /><span className="sr-only">Delete {data.filename}</span></ErrorButton> },
                            ]}
                            overlayNoRowsTemplate="No context files yet."
                        />
                        {busy && busy !== '__upload__' && <p role="status" className="text-sm mt-2">Removing file…</p>}
                    </>}
                    {error && <p role="alert" className="text-sm text-danger mt-3">{error}</p>}
                    {!project && error && <SecondaryButton size="sm" handleClick={() => setReload((value) => value + 1)}>Retry</SecondaryButton>}
                </>}
                <div className="flex justify-end mt-4"><SecondaryButton size="sm" disable={!!busy} handleClick={onClose}>Close</SecondaryButton></div>
            </div>
        </PrimaryModal>
    );
}
